import { Link } from 'react-router';
import { Icon, Skeleton } from '../../ui';
import { useFieldContext } from '../FieldContext';
import { useRelationLabel } from '../useRelationLabel';
import { CELL_AFFORDANCE, CellValueWithAffordance, stopCellClick } from './cellAffordance';
import { EmptyValue } from './EmptyValue';

/**
 * A relation renders as the target's title and links to it. While the target is
 * loading it shows a skeleton of the same height, never the raw uuid: an id in a
 * cell is noise a user cannot act on.
 *
 * TWO WAYS TO KNOW THE TITLE, and the cheap one wins (D3). A list query already
 * resolved every relation label in ONE statement per relation field, and that answer
 * arrives on the context; only a single-record read - which returns bare ids - has to
 * ask, and then it asks through the canonical cache so five rows pointing at one
 * agency still make one request.
 */
export function RelationDisplay() {
  const { field, value, relationLabel, linkAsIcon, listRow } = useFieldContext();
  const recordId = typeof value === 'string' ? value : null;
  const targetObjectId = field.options?.relation?.targetObjectId;
  /**
   * The fallback is DISABLED when the label is already known, so the resolved case
   * costs no query at all rather than one that is merely ignored. `useRelationLabel`
   * enables itself on `recordId`, so passing null is what turns it off - and it is a
   * hook, so it cannot be skipped conditionally.
   */
  const resolved = useRelationLabel(field, relationLabel === undefined ? recordId : null);
  const label = relationLabel ?? resolved.label;

  if (recordId === null) {
    return <EmptyValue />;
  }
  if (relationLabel === undefined && resolved.isPending) {
    return <Skeleton className="w-24" />;
  }
  if (label === undefined || targetObjectId === undefined) {
    return <EmptyValue />;
  }

  const to = `/objects/${targetObjectId}/records/${recordId}`;

  // In an EDITABLE cell the name is plain text and the record it points at is one icon
  // away (P1-8): the cell's own click has to reach the cell, which is the editor.
  if (linkAsIcon === true) {
    return (
      <CellValueWithAffordance
        affordance={
          <Link
            to={to}
            aria-label={`Open ${label}`}
            className={CELL_AFFORDANCE}
            onClick={stopCellClick}
          >
            <Icon name="chevron-right" size="0.85rem" />
          </Link>
        }
      >
        {label}
      </CellValueWithAffordance>
    );
  }

  // IN A LIST ROW THE ROW IS THE LINK (P4 round 2, finding F5): a phone row's secondary line
  // showed the owner's name in the link blue while the row itself was already a link to the
  // record, so one row carried two destinations and one of them was invisible.
  if (listRow === true) {
    return <span className="truncate">{label}</span>;
  }

  // No underline under the pointer: the rule and its source are in `UrlDisplay`.
  return (
    <Link to={to} className="block truncate text-accent" onClick={stopCellClick}>
      {label}
    </Link>
  );
}
